import { Button, cx } from '@ims/ui'
import type { ReactNode } from 'react'

/**
 * One titled part of a settings form: what it is on the left, its controls on the right
 * (a two-column grid; `sm:col-span-2` for a full row), stacked on narrower screens.
 */
export function SettingsPart({
  title,
  about,
  children,
}: {
  title: string
  about: ReactNode
  children: ReactNode
}) {
  return (
    <section className="flex flex-col gap-x-8 gap-y-3 border-t border-border p-4 first:border-t-0 sm:p-6 xl:flex-row">
      <div className="xl:w-60 xl:shrink-0">
        <h3 className="font-semibold text-ink">{title}</h3>
        <p className="mt-0.5 text-sm text-muted">{about}</p>
      </div>
      <div className="grid min-w-0 flex-1 content-start gap-4 sm:grid-cols-2">{children}</div>
    </section>
  )
}

/**
 * The foot of a settings form that waits for Save (Business, Product fields): Save at the end
 * of the form, and while there are changes a bar that floats above the bottom bar with
 * "You have changes to save.", Discard and Save, so Save is always in view.
 */
export function SaveBar({
  dirty,
  busy,
  onSave,
  onDiscard,
  saveLabel = 'Save',
  disabled = false,
  inCard = false,
}: {
  dirty: boolean
  busy: boolean
  onSave: () => void
  onDiscard: () => void
  saveLabel?: string
  disabled?: boolean
  /** Inside a padded `Card` (Product fields), not at the foot of a `p-0` one (Business). */
  inCard?: boolean
}) {
  return (
    <div
      className={cx(
        'flex items-center gap-3',
        inCard ? 'mt-6' : 'mx-4 mb-4 sm:mx-6 sm:mb-6',
        dirty &&
          'sticky bottom-24 z-10 rounded-xl border border-border bg-raised p-3 shadow-card-hover lg:bottom-4',
      )}
    >
      {dirty ? (
        <span className="flex min-w-0 flex-1 items-center gap-2 text-sm text-muted">
          <span className="size-2 shrink-0 rounded-full bg-warning" />
          You have changes to save.
        </span>
      ) : (
        <span className="flex-1" />
      )}
      {dirty && (
        <Button variant="ghost" disabled={busy} onClick={onDiscard}>
          Discard
        </Button>
      )}
      <Button loading={busy} disabled={disabled} onClick={onSave}>
        {saveLabel}
      </Button>
    </div>
  )
}
